import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminBookingsApi } from '@api/client';
import type { Booking, BookingFilters, PaginatedResponse } from '@api/types';

const BOOKINGS_KEY = 'bookings';

function filtersToParams(filters: BookingFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.centerId) params.centerId = filters.centerId;
  if (filters.status) params.status = filters.status;
  if (filters.serviceId) params.serviceId = filters.serviceId;
  if (filters.staffId) params.staffId = filters.staffId;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.q) params.q = filters.q;
  if (filters.page !== undefined) params.page = String(filters.page);
  if (filters.perPage !== undefined) params.perPage = String(filters.perPage);
  return params;
}

export function useBookings(filters: BookingFilters = {}) {
  return useQuery({
    queryKey: [BOOKINGS_KEY, 'list', filters],
    queryFn: () =>
      adminBookingsApi.list(filtersToParams(filters)),
    staleTime: 30_000,
  });
}

/**
 * Booking history for a single customer (used by CustomersPage aside). Returns
 * the most recent `perPage` bookings, sorted DESC by startTime. `enabled` is
 * gated on the customerId so the hook is safe to call with `null` (the aside
 * may not have a selection yet).
 */
export function useCustomerBookings(customerId: string | null, perPage = 10) {
  return useQuery({
    queryKey: [BOOKINGS_KEY, 'by-customer', customerId, perPage],
    enabled: !!customerId,
    queryFn: () =>
      adminBookingsApi.list({
        customerId: customerId ?? '',
        perPage: String(perPage),
      }),
    staleTime: 30_000,
  });
}

export function useBooking(id: string) {
  return useQuery({
    queryKey: [BOOKINGS_KEY, 'detail', id],
    queryFn: () => adminBookingsApi.get(id),
    enabled: !!id,
  });
}

export function useBookingHistory(id: string) {
  return useQuery({
    queryKey: [BOOKINGS_KEY, 'history', id],
    queryFn: () => adminBookingsApi.history(id),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      adminBookingsApi.cancel(id, reason),
    // Optimistic update
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: [BOOKINGS_KEY] });
      const previous = qc.getQueriesData<PaginatedResponse<Booking>>({
        queryKey: [BOOKINGS_KEY, 'list'],
      });
      qc.setQueriesData<PaginatedResponse<Booking>>(
        { queryKey: [BOOKINGS_KEY, 'list'] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((b) => (b.id === id ? { ...b, status: 'cancelled' } : b)),
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        ctx.previous.forEach(([key, data]) => {
          qc.setQueryData(key, data);
        });
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
    },
  });
}

export function useApproveBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminBookingsApi.approve(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
  });
}

export function useRejectBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminBookingsApi.reject(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
  });
}

export function useCompleteBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminBookingsApi.complete(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
  });
}

export function useCheckInBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminBookingsApi.checkIn(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
  });
}

export function useNoShowBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminBookingsApi.noShow(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
  });
}

export function useRescheduleBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newStartTime, staffId }: { id: string; newStartTime: string; staffId?: string }) =>
      adminBookingsApi.reschedule(id, newStartTime, staffId),
    onSettled: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
  });
}

/** Generic admin update — used for editing the staff note from the drawer. */
export function useUpdateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminBookingsApi.update(id, data),
    onSettled: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
  });
}
